/** @jsx jsx */

/** @jsxFrag React.Fragment  */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import {
  Avatar,
  Button,
  Dialog,
  Divider,
  Grid,
  Hidden,
  Skeleton,
  Typography,
  Typography2,
  breakpoints,
} from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import {
  formatCourseEnrollmentsProgressData,
  useGetCourseEnrollmentsWithProgressDuringFreeTrial,
} from 'bundles/enroll/utils/blockCertInFreeTrialUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/refund';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const useStyles = () => {
  return {
    dialogContainer: css`
      position: relative;

      .cds-Dialog-dialog {
        max-width: 720px !important;
      }
    `,
    dividerSpacing: css`
      margin-bottom: var(--cds-spacing-300);
      margin-top: var(--cds-spacing-400);
    `,
    listSpacing: css`
      margin: var(--cds-spacing-200) 0;
    `,
    courseInfoContainer: css`
      margin-bottom: var(--cds-spacing-300);
      margin-top: var(--cds-spacing-100);

      ${breakpoints.up('sm')} {
        gap: var(--cds-spacing-100);
      }
    `,
    actionsContainer: css`
      display: flex;
      justify-content: flex-end;
      flex-wrap: wrap;
      gap: var(--cds-spacing-200);
    `,
    moreCoursesContainer: css`
      margin-bottom: var(--cds-spacing-400);
      gap: var(--cds-spacing-100);
    `,
    partnerLogosContainer: css`
      display: flex;

      > *:not(:last-child) {
        margin-right: -16px;
      }
    `,
    marginBottom8: css`
      margin-bottom: 8px;
    `,
  };
};

type Props = {
  handleClose: () => void;
  handleContinue: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
  subscriptionId?: string;
  productType: 'CourseraPlusSubscription' | 'SpecializationSubscription';
  productItemId?: string;
  isLoading: boolean;
};

const BlockCertinFreeTrialCancelSubscriptionModal = ({
  handleClose,
  handleContinue,
  productType,
  productItemId,
  subscriptionId,
  isLoading,
}: Props) => {
  const styles = useStyles();
  const { loading, courseEnrollments } = useGetCourseEnrollmentsWithProgressDuringFreeTrial({
    productType,
    productItemId,
  });

  const courseData = formatCourseEnrollmentsProgressData(courseEnrollments);

  const { HeadingGroup, Content, Actions } = Dialog;
  const courseSectionLoadingComponent = () => (
    <div
      css={{
        width: '100%',
      }}
      data-testid="loading-component"
    >
      <Typography2 component="p" variant="bodyPrimary" css={styles.marginBottom8}>
        <Skeleton width="30%" />
      </Typography2>
      <div css={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <Skeleton variant="rect" width={80} height={64} css={styles.marginBottom8} />
        <div css={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          <Typography variant="h2">
            <Skeleton width="50%" />
          </Typography>
          <Typography2 component="p" variant="bodyPrimary">
            <Skeleton width="70%" />
          </Typography2>
        </div>
      </div>
      <div css={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 16 }}>
        <Skeleton variant="circle" width={36} height={36} css={styles.marginBottom8} />
        <Skeleton variant="rect" width="30%" height={16} css={styles.marginBottom8} />
      </div>
      <Divider color="light" css={styles.dividerSpacing} />
    </div>
  );
  const actionButtons = (isFullWidth: boolean) => {
    return (
      <div css={styles.actionsContainer}>
        <TrackedButton
          size="medium"
          trackingName="block_cert_cancel_subscription_button"
          data-testid="confirm-cancel-subscription-button"
          variant="secondary"
          fullWidth={isFullWidth}
          onClick={handleContinue}
          disabled={isLoading}
          trackingData={{ productType, subscriptionId }}
        >
          {_t(`Yes, cancel subscription`)}
        </TrackedButton>
        <TrackedButton
          size="medium"
          variant="primary"
          trackingName="block_cert_keep_subscription_button"
          data-testid="keep-subscription-button"
          fullWidth={isFullWidth}
          trackingData={{ productType, subscriptionId }}
          onClick={handleClose}
          disabled={isLoading}
        >
          {_t(`No, keep subscription`)}
        </TrackedButton>
      </div>
    );
  };
  const courseSection = () => {
    if (loading) {
      return courseSectionLoadingComponent();
    }
    if (courseData) {
      return (
        <div data-testid="course-section">
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedMessage
              message={_t(`{logoCount, plural, =0 {Course} other {Courses}} you have started`)}
              logoCount={courseData.partnerLogos?.length || 0}
            />
          </Typography2>
          <Grid container css={styles.courseInfoContainer}>
            <Grid item>
              <Hidden xsDown>
                <Avatar
                  variant="organization"
                  size={64}
                  showBorder={true}
                  imgProps={{
                    src: courseData.courseLogo,
                  }}
                />
              </Hidden>
            </Grid>
            <Grid item>
              <Typography variant="h2">{courseData.courseName}</Typography>
              <Typography2 component="p" variant="bodyPrimary" color="supportText">
                {courseData.partnerName}
              </Typography2>
            </Grid>
          </Grid>
          {courseData?.partnerLogos && (
            <Grid container alignItems="center" css={styles.moreCoursesContainer}>
              <Grid item css={styles.partnerLogosContainer}>
                {courseData.partnerLogos.map((partnerLogo) => (
                  <Avatar
                    variant="user"
                    size={36}
                    showBorder={true}
                    imgProps={{
                      src: partnerLogo,
                    }}
                    style={{ backgroundColor: 'white' }}
                  />
                ))}
              </Grid>

              <Typography2 component="p" variant="bodyPrimary" color="supportText">
                <FormattedMessage
                  message={_t(`and {courseCount} more {courseCount, plural, =1 {course} other {courses}}`)}
                  courseCount={courseData.countOfRemainingCourses}
                />
              </Typography2>
            </Grid>
          )}
          <Divider color="light" css={styles.dividerSpacing} />
        </div>
      );
    }
    return null;
  };
  return (
    <Dialog
      aria-describedby="dialog-content"
      open={true}
      sticky="headingGroup"
      variant="standard"
      width="large"
      css={styles.dialogContainer}
      onClose={handleClose}
    >
      <HeadingGroup data-testid="dialog-heading">
        {_t('Are you sure you want to cancel your subscription?')}
      </HeadingGroup>
      <Content id="dialog-content">
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking={true}
          trackingName="block_cert_in_free_trial_cancel_subscription_modal"
          requireFullyVisible={false}
          data={{ subscriptionId, productType }}
        >
          {courseSection()}
          <ul data-testid="value-props">
            <Typography2 variant="bodyPrimary" component="li" css={styles.listSpacing}>
              {_t(
                `If you cancel, you won't get any certificates and you'll lose access to all graded course content when your free trial ends.`
              )}
            </Typography2>
            <Typography2 variant="bodyPrimary" component="li" css={styles.listSpacing}>
              {_t(
                `If you continue, your subscription will start after your free trial ends and you'll get any certificates you've earned.`
              )}
            </Typography2>
            <Typography2 variant="bodyPrimary" component="li" css={styles.listSpacing}>
              {_t(`Your course progress will be saved if you decide to re-enroll later.`)}
            </Typography2>
          </ul>
        </TrackedDiv>
      </Content>
      <Actions>
        <Hidden xsDown>{actionButtons(false)}</Hidden>
        <Hidden smUp>{actionButtons(true)}</Hidden>
      </Actions>
    </Dialog>
  );
};

export default BlockCertinFreeTrialCancelSubscriptionModal;
